const PENDING_EMAIL_KEY = 'myl_email_link_address';

/**
 * Exchange the `e` token from a sign-in link for the address it was issued to.
 *
 * The address isn't in the link as plaintext because the URL reaches browser
 * history, logs, and analytics. Returns null if the token is missing, expired,
 * or tampered with, in which case the caller falls back to asking.
 */
export const resolveEmailForLink = async (token: string): Promise<string | null> => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/auth/email/resolve-link`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      }
    );
    if (!response.ok) return null;
    const data = await response.json();
    return data.email || null;
  } catch {
    return null;
  }
};

/**
 * Firebase requires the email address as a second factor when redeeming a
 * sign-in link, and deliberately doesn't carry it in the link itself. Without
 * that check an attacker could mail their own link to a victim, who would be
 * silently signed into the attacker's account and start filling it with their
 * own data. Requiring the address proves the person redeeming the link is the
 * one who asked for it.
 *
 * So we stash it when we request the email and read it back on redemption. That
 * covers same-browser clicks; anything else (a phone, a webmail in-app browser)
 * has separate storage and gets asked for the address instead.
 */
export const rememberEmailForLink = (email: string): void => {
  try {
    window.localStorage.setItem(PENDING_EMAIL_KEY, email);
  } catch {
    // Private browsing or a full quota. The redemption page prompts instead.
  }
};

export const recallEmailForLink = (): string | null => {
  try {
    return window.localStorage.getItem(PENDING_EMAIL_KEY);
  } catch {
    return null;
  }
};

export const forgetEmailForLink = (): void => {
  try {
    window.localStorage.removeItem(PENDING_EMAIL_KEY);
  } catch {
    // Nothing to do; a stale value is only ever used as a prefill.
  }
};
